/**
 * @file ProductsListPage.tsx
 * @description Реестр товаров (phase19.5; phase19.31 — макет (а) разбора B:
 * таблица от 1024, компактный список с миниатюрой на телефоне, плитки по
 * переключателю, постраничный вывод).
 *
 * Страница — раскладка: заголовок в верхней панели (`CabinetPageHeader`,
 * «N товаров · M на сайте», главное действие «+ Товар»), фильтры, список,
 * страницы. Данные и состояние в адресе — `useProductsRegistry`, колонки —
 * `buildProductColumns`, меню строки — `useProductRowActions`.
 *
 * Категории (SC52) — фильтром «Категория ▾» с деревом; боковая панель
 * категорий — только с 1600px, шириной 296px (замечание владельца: в 240px
 * обрезались длинные названия), на ней же выбирается отбор.
 */

import { Plus } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Link, useParams } from "react-router";

import { CategoryTreeSidebar } from "../../../components/catalog/CategoryTreeSidebar";
import { ProductCardsGrid } from "../../../components/catalog/ProductCardsGrid";
import { useProductKinds } from "../../../components/catalog/product-kinds/useProductKinds";
import { buildProductColumns } from "../../../components/catalog/registry/product-columns";
import { productsEmptySpec, productsFilteredSpec } from "../../../components/catalog/registry/products-empty";
import { ProductsCompactList } from "../../../components/catalog/registry/ProductsCompactList";
import { ProductsFilterBar } from "../../../components/catalog/registry/ProductsFilterBar";
import { ProductsPagination } from "../../../components/catalog/registry/ProductsPagination";
import { useProductRowActions } from "../../../components/catalog/registry/useProductRowActions";
import { useProductsRegistry } from "../../../components/catalog/registry/useProductsRegistry";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { ListError, LIST_SURFACE_CLASS } from "../../../components/ui/ListStates";
import { NoticeStrip } from "../../../components/ui/NoticeStrip";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import type { CatalogProduct } from "../../../lib/api-catalog";
import { productsFiltered } from "../../../lib/catalog-registry-query";
import { pluralRu } from "../../../lib/plural";
import { cn } from "@/lib/utils";

/** Боковая панель категорий — только на очень широком экране (SC52). */
const CATEGORY_PANEL_QUERY = "(min-width: 1600px)";

export function ProductsListPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = `/shop/${slug}/catalog/products`;
  const registry = useProductsRegistry();
  const { query, setQuery, page } = registry;
  const { kinds } = useProductKinds();
  const belowLg = useMediaQuery(BELOW_LG_QUERY);
  const categoryPanel = useMediaQuery(CATEGORY_PANEL_QUERY);

  const catById = useMemo(() => new Map(registry.categories.map((c) => [c.id, c])), [registry.categories]);
  const columns = useMemo(() => buildProductColumns(catById), [catById]);
  const productHref = useCallback((p: CatalogProduct) => `${base}/${p.id}`, [base]);
  const actions = useProductRowActions(productHref, registry.refresh);
  const newHref = `${base}/new${query.categoryId ? `?category=${encodeURIComponent(query.categoryId)}` : ""}`;
  const filtered = productsFiltered(query);
  const ownLayout = page.items.length > 0 && (query.view === "tiles" || belowLg);

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Товары"
        subtitle={
          registry.total === 0 && (registry.loading || registry.error)
            ? undefined
            : `${registry.total} ${pluralRu(registry.total, ["товар", "товара", "товаров"])} · ${registry.onSite} на сайте`
        }
        action={
          <Button variant="brand" asChild>
            <Link to={newHref}>
              <Plus className="size-4" />
              Товар
            </Link>
          </Button>
        }
      />

      <div className={cn("grid grid-cols-1 gap-4", categoryPanel && "grid-cols-[296px_minmax(0,1fr)]")}>
        {categoryPanel ? (
          <aside className={cn(LIST_SURFACE_CLASS, "self-start rounded-xl p-2 [box-shadow:inset_0_0_0_1px_var(--border)]")}>
            <CategoryTreeSidebar
              categories={registry.categories}
              products={registry.products}
              selectedCategoryId={query.categoryId || null}
              onSelect={(id) => setQuery({ categoryId: id ?? "" })}
            />
          </aside>
        ) : null}

        <div className="flex min-w-0 flex-col gap-4">
          <ProductsFilterBar
            query={query}
            onChange={setQuery}
            onReset={registry.resetFilters}
            categories={registry.categories}
            kinds={kinds}
            chipCounts={registry.selection.chipCounts}
            categoryPanel={categoryPanel}
          />

          {registry.stockMissing ? (
            <NoticeStrip tone="info">Остатки временно недоступны — список товаров показан без них</NoticeStrip>
          ) : null}

          {ownLayout ? (
            <div className="flex flex-col gap-3">
              {registry.error ? <ListError message={registry.error} onRetry={registry.refresh} /> : null}
              {query.view === "tiles" ? (
                <ProductCardsGrid products={page.items} categoriesById={catById} rowHref={productHref} rowActions={actions.rowActions} />
              ) : (
                <ProductsCompactList rows={page.items} categoriesById={catById} rowHref={productHref} rowActions={actions.rowActions} />
              )}
            </div>
          ) : (
            <ResponsiveList
              aria-label="Реестр товаров"
              rows={page.items}
              columns={columns}
              rowKey={(p) => p.id}
              rowHref={productHref}
              rowLabel={(p) => `Товар ${p.displayName}`}
              rowActions={actions.rowActions}
              rowActionsTitle={(p) => `Действия: ${p.displayName}`}
              rowSize="double"
              tableMinWidth="min-w-[860px]"
              loading={registry.loading}
              error={registry.error}
              onRetry={registry.refresh}
              filtered={filtered}
              skeletonRows={8}
              empty={productsEmptySpec(newHref)}
              filteredEmpty={productsFilteredSpec(registry.resetFilters)}
            />
          )}

          <ProductsPagination
            page={page.page}
            pages={page.pages}
            from={page.from}
            to={page.to}
            total={registry.selection.rows.length}
            pageSize={query.pageSize}
            onPageChange={(next) => setQuery({ page: next })}
            onPageSizeChange={(pageSize) => setQuery({ pageSize })}
          />
        </div>
      </div>
      <ConfirmDialog request={actions.confirmRequest} onSettle={actions.settleConfirm} />
    </div>
  );
}
